import React from "react";
import { Image as ImageIcon, AddPhotoAlternate as ImagePlus, Refresh as RefreshCw, ErrorOutline as ErrorIcon } from "@mui/icons-material";
import { FEATURE_CONFIG } from "../../../../utils/imageEditor";
import styles from "../AiImageGenerator.module.css";
import ResultCard from "./ResultCard";
import LoadingState from "./LoadingState";

export function PreviewPanel({
	mode,
	generatedUrls,
	processedUrls,
	previewUrl,
	loading,
	error,
	selectedFeatures,
	hasGeneratedResults,
	hasProcessedResults,
	onResetGenerated,
	onResetProcessed,
	onDownload,
}) {
	const previewTitle =
		mode === "generate"
			? generatedUrls.length > 0
				? "Generated results"
				: "Preview"
			: processedUrls.length > 0
			? "Original & results"
			: "Preview";

	const loadingMessage =
		mode === "generate"
			? "Generating 3 images…"
			: selectedFeatures.length === 1
			? `Applying ${FEATURE_CONFIG[selectedFeatures[0]].label}…`
			: `Applying ${selectedFeatures.length} features…`;

	return (
		<div className={styles.previewPanel}>
			<div className={styles.previewHeader}>
				<h2 className={styles.panelHeadingNoMargin}>
					<ImageIcon sx={{ fontSize: 16, color: "var(--aig-dark)" }} />
					{previewTitle}
				</h2>

				{mode === "generate"
					? generatedUrls.length > 0 && (
							<button onClick={onResetGenerated} className={styles.resetBtn}>
								<RefreshCw sx={{ fontSize: 14 }} />
								Reset
							</button>
					  )
					: processedUrls.length > 0 && (
							<button onClick={onResetProcessed} className={styles.resetBtn}>
								<RefreshCw sx={{ fontSize: 14 }} />
								Reset
							</button>
					  )}
			</div>

			<div className={styles.previewBody}>
				{error ? (
					<div className={styles.previewErrorState}>
						<ErrorIcon sx={{ fontSize: 48 }} className={styles.previewErrorIcon} />
						<p className={styles.previewErrorText}>{error}</p>
					</div>
				) : mode === "generate" && generatedUrls.length > 0 ? (
					<div className={styles.resultsGrid}>
						<div className={styles.resultsGridInner}>
							{generatedUrls.map((url, i) => (
								<ResultCard
									key={i}
									title={`Result ${i + 1}`}
									url={url}
									index={i}
									onDownload={onDownload}
								/>
							))}
						</div>
					</div>
				) : loading ? (
					<LoadingState message={loadingMessage} />
				) : mode === "generate" ? (
					<div className={styles.emptyStateGenerate}>
						<ImagePlus sx={{ fontSize: 64, color: "var(--aig-dark)" }} />
						<p className={styles.emptyHelp}>
							Enter a prompt above and click{" "}
							<span className={styles.emptyHelpAccent}>Generate image</span>. Same
							request is sent 3 times; all results shown here.
						</p>
					</div>
				) : processedUrls.length > 0 && previewUrl ? (
					<div className={styles.resultsGrid}>
						<div className={styles.resultsGridInner}>
							<div className={styles.originalCard}>
								<p className={styles.originalCardLabel}>Original</p>
								<div className={styles.originalCardBody}>
									<img
										src={previewUrl}
										alt="Original"
										className={styles.originalCardImg}
										draggable={false}
									/>
								</div>
							</div>
							{processedUrls.map((url, i) => (
								<ResultCard
									key={i}
									title={`Result ${i + 1}`}
									url={url}
									index={i}
									onDownload={onDownload}
								/>
							))}
						</div>
					</div>
				) : previewUrl ? (
					<div className={styles.uploadedPreview}>
						<div className={styles.uploadedPreviewBox}>
							<img
								src={previewUrl}
								alt="Uploaded"
								className={styles.uploadedPreviewImg}
							/>
						</div>
						<p className={styles.emptyHelp}>
							Select a feature and click{" "}
							<span className={styles.emptyHelpAccent}>Apply Feature</span> to
							process the image.
						</p>
					</div>
				) : (
					<div className={styles.emptyState}>
						<ImageIcon sx={{ fontSize: 64 }} />
						<p className={styles.emptyHelp}>Upload an image to get started</p>
					</div>
				)}
			</div>

			{(hasProcessedResults || hasGeneratedResults) && (
				<p className={styles.noteFooter}>
					{mode === "generate"
						? "Same request sent 3 times. Use Download on each result."
						: "Same request sent 3 times. 2×2: Original + Result 1, 2, 3. Use Download on each."}
				</p>
			)}
		</div>
	);
}
